import { getStoryRowOrder } from '@aboutyou/legacy-shared/src/utils/storyRowOrder'
import {
  DEFAULT_PRODUCT_SORTING_ID,
  ProductSortingIdType,
} from 'constants/sorting'
import { exists } from 'helpers/exists'
import { IService } from 'services'
import {
  CommonStoryRow,
  StoryRow,
} from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.types'
import { AdditionalTile } from 'sharedComponents/ProductStream/BatchLoader'
import Story from 'store/entities/Story/Story.store'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'
import RowBuilder, {
  InterleaveRow,
  RowTypes,
} from 'uiComponents/molecules/TileGrid/RowBuilder'

export const computeSelectedCategoryId = ({
  rootCategoryId,
  service,
}: {
  rootCategoryId: string
  service: IService
}): string => {
  const findSelectedCategoryId = (categoryId: string): string => {
    const category = service.categories.getById(categoryId)
    const categoryChildrenIds = category?.childrenIds ?? []

    if (categoryChildrenIds.length === 1) {
      return findSelectedCategoryId(categoryChildrenIds[0])
    }

    return categoryId
  }

  return findSelectedCategoryId(rootCategoryId)
}

const repositionTiles = (
  interleaveRows: InterleaveRow[],
  storyRowProductTiles: AdditionalTile[][],
  tilesPerRow: number,
) => {
  return storyRowProductTiles.map((tiles, ithRow) => {
    const currentRowIndex = interleaveRows[ithRow].index
    const currentFirstTilePosition = (currentRowIndex - ithRow) * tilesPerRow
    return tiles.map((tile, tileIndex) => ({
      ...tile,
      position: currentFirstTilePosition + tileIndex,
    }))
  })
}

const repositionRows = (
  rawInterleaveRows: InterleaveRow[],
  extraProductsRowCounts: number[],
) => {
  let rowsShifted = 0
  const repositionedRows = []

  for (let i = 0; i < rawInterleaveRows.length; i++) {
    const row = rawInterleaveRows[i]

    if (i === 0) {
      repositionedRows.push(row)
      continue
    }

    rowsShifted += extraProductsRowCounts[i - 1]

    repositionedRows.push({
      ...row,
      index: row.index + rowsShifted,
    })
  }

  return repositionedRows
}

const getProductTilesForRow =
  (tilesPerRow: number) => (interleaveRow: InterleaveRow) => {
    const { index } = interleaveRow
    const row = interleaveRow.row as unknown as StoryRow

    // there should be a maximum of 5 product rows between editorial rows
    // but in case it's the first orphaned row, limit additional products to max. 2 rows
    // so the first content elements can still be seen quickly
    const maxAdditionalTileCount = isOrphanedProductRow(row)
      ? tilesPerRow * 2
      : tilesPerRow * 5

    if (
      row.type === 'idol-story-outfit-header' ||
      row.productIds.length === 0
    ) {
      return []
    }

    const tiles: AdditionalTile[] = row.productIds
      .map((productId, i) => ({
        id: productId,
        type: 'product' as const,
        position: index * tilesPerRow + i,
      }))
      .slice(0, maxAdditionalTileCount)

    return tiles
  }

const fillWithPlaceholders = (
  additionalProductTiles: AdditionalTile[][],
  isIdolStory: boolean,
  productCount: number,
  tilesPerRow: number,
) => {
  const additionalTiles = additionalProductTiles.flatMap(tiles => {
    if (tiles.length === 0) {
      return tiles
    }

    const overlappingTiles = tiles.length % tilesPerRow
    const lastTilePosition = tiles[tiles.length - 1].position

    const regularStoryStillHasEnoughProducts =
      !isIdolStory && productCount > lastTilePosition

    if (overlappingTiles === 0 || regularStoryStillHasEnoughProducts) {
      return tiles
    }

    const tilesWithPlaceholders = [...tiles]

    const placeholdersToPushCount = tilesPerRow - overlappingTiles

    for (let i = 0; i < placeholdersToPushCount; i++) {
      const position = lastTilePosition + i + 1

      tilesWithPlaceholders.push({
        id: `placeholder-${position}'`,
        type: 'placeholder',
        position,
      })
    }

    return tilesWithPlaceholders
  })

  if (additionalTiles.length === 0) {
    return []
  }

  const firstPos = additionalTiles[0].position

  // Now prepend placeholders, in case there are less products
  // than the first positioned product tile
  if (!isIdolStory) {
    let i = 0

    for (let position = productCount; position < firstPos; position++) {
      additionalTiles.splice(i, 0, {
        id: `placeholder-${position}`,
        type: 'placeholder',
        position,
      })

      i++
    }
  }

  return additionalTiles
}

type StoryRowOrder = ReturnType<typeof getStoryRowOrder>

export function getInterleaveRowsAndProductTiles({
  productCount,
  tilesPerRow,
  storyRows,
  isIdolStory,
}: {
  productCount: number
  tilesPerRow: number
  storyRows: StoryRow[]
  isIdolStory: boolean
}) {
  const hasOrphanedProductRows = includesOrphanedProducts(storyRows)
  let rowOrder = getStoryRowOrder({
    productCount,
    contentRowCount: hasOrphanedProductRows
      ? storyRows.length - 1
      : storyRows.length,
    productsPerRow: tilesPerRow,
  })

  if (hasOrphanedProductRows) {
    rowOrder = injectOrphanedRowIntoRowOrder(rowOrder)
  }

  const rawInterleaveRows = getRawInterleaveRows({
    rowOrder,
    storyRows,
    isIdolStory,
  })

  const storyRowProductTiles: AdditionalTile[][] = rawInterleaveRows.map(
    getProductTilesForRow(tilesPerRow),
  )

  const storyRowProductsRowCounts = storyRowProductTiles.map((tiles, index) => {
    const maxExtraRows = Math.ceil(tiles.length / tilesPerRow)

    if (maxExtraRows === 0) {
      return 0
    }

    const interleaveRow = rawInterleaveRows[index]

    return (
      maxExtraRows -
      countProductRows({
        currentRowIndex: interleaveRow.index,
        maxExtraRows,
        rowOrder,
      })
    )
  })

  const interleaveRows = repositionRows(
    rawInterleaveRows,
    storyRowProductsRowCounts,
  )

  const additionalTileCount = storyRowProductTiles.reduce(
    (a, b) => a + b.length,
    0,
  )

  if (additionalTileCount === 0) {
    return { interleaveRows, additionalTiles: [] }
  }

  const additionalProductTiles = repositionTiles(
    interleaveRows,
    storyRowProductTiles,
    tilesPerRow,
  )

  const additionalTiles = fillWithPlaceholders(
    additionalProductTiles,
    isIdolStory,
    productCount,
    tilesPerRow,
  )

  if (hasOrphanedProductRows) {
    return {
      interleaveRows: deleteOrphanedProductRowFromInterleaveRow(interleaveRows),
      additionalTiles,
    }
  }

  return { interleaveRows, additionalTiles }
}

function getInterleaveRowsAndProductTilesForNoProductRows({
  storyRows,
}: {
  storyRows: StoryRow[]
}) {
  const interleaveRows = storyRows.map((row, index) => ({
    index,
    row: {
      ...row,
      componentType: RowTypes.EditorialRow,
      rowIndex: index,
    },
  }))

  const additionalTiles: AdditionalTile[] = storyRows
    .filter(
      (row): row is CommonStoryRow => row.type !== 'idol-story-outfit-header',
    )
    .flatMap(row =>
      row.productIds.map(productId => ({
        id: productId,
        type: 'product' as const,
        position: 0,
      })),
    )
    .map((tile, i) => ({
      ...tile,
      position: i,
    }))

  return { interleaveRows, additionalTiles }
}

const countProductRows = ({
  currentRowIndex,
  maxExtraRows,
  rowOrder,
}: {
  maxExtraRows: number
  currentRowIndex: number
  rowOrder: StoryRowOrder
}) => {
  let existingFollowupProductRows = 0
  const firstFollowUpRowIndex = currentRowIndex + 1

  for (
    let i = firstFollowUpRowIndex;
    i < firstFollowUpRowIndex + maxExtraRows;
    i++
  ) {
    const followUpRow = rowOrder[i]
    if (exists(followUpRow) && followUpRow.type === 'content') {
      break
    }

    existingFollowupProductRows++
  }

  return existingFollowupProductRows
}

function getRawInterleaveRows({
  storyRows,
  isIdolStory,
  rowOrder,
}: {
  storyRows: StoryRow[]
  isIdolStory: boolean
  rowOrder: StoryRowOrder
}): InterleaveRow[] {
  const interleaveRows = isIdolStory
    ? createIdolInterleaveRows(storyRows)
    : createStoryInterleaveRows({
        rowOrder,
        storyRows,
      })

  if (interleaveRows.length !== storyRows.length) {
    throw new Error(
      `Normalized story rows must alway have the same length as interleave content rows. Instead got ${storyRows.length} story rows and ${interleaveRows.length} interleave rows.`,
    )
  }

  return interleaveRows
}

function createStoryInterleaveRows({
  storyRows,
  rowOrder,
}: {
  storyRows: StoryRow[]
  rowOrder: StoryRowOrder
}) {
  return rowOrder
    .filter(({ type }) => type !== 'product')
    .map(({ index: insertIndex }, storyIndex) => {
      return {
        index: insertIndex,
        row: {
          ...storyRows[storyIndex],
          componentType: RowTypes.EditorialRow,
          rowIndex: insertIndex,
        },
      }
    })
}

function createIdolInterleaveRows(storyRows: StoryRow[]) {
  return storyRows.map((_, i) => ({
    index: i,
    row: {
      ...storyRows[i],
      componentType: RowTypes.EditorialRow,
      rowIndex: i,
    },
  }))
}

export interface StoryStreamViewStore {
  rowBuilder: RowBuilder
  story: Story | null
  storyRows: StoryRow[]
  pagination: PaginationViewStore
  setStoryRowProductTiles: (tiles: AdditionalTile[]) => void
}

export function updateInterleaveRowsAndProductTiles(
  storyPageStore: StoryStreamViewStore,
  isEditorialPresent: boolean,
) {
  if (isEditorialPresent && storyPageStore.pagination.currentPage === 1) {
    const { interleaveRows, additionalTiles } = storyPageStore.story
      ?.noProductRows
      ? getInterleaveRowsAndProductTilesForNoProductRows({
          storyRows: storyPageStore.storyRows,
        })
      : getInterleaveRowsAndProductTiles({
          productCount: Math.min(
            storyPageStore.pagination.total,
            PaginationViewStore.productsPerPage,
          ),
          tilesPerRow: storyPageStore.rowBuilder.tilesPerRow,
          storyRows: storyPageStore.storyRows,
          isIdolStory: storyPageStore.story
            ? storyPageStore.story.isIdolStory
            : false,
        })

    storyPageStore.rowBuilder.resetInterleaveRows(interleaveRows)
    storyPageStore.setStoryRowProductTiles(additionalTiles)
  } else {
    storyPageStore.rowBuilder.resetInterleaveRows([])
    storyPageStore.setStoryRowProductTiles([])
  }
}

const isOrphanedProductRow = (row: StoryRow) =>
  row.type === 'orphaned product row'

const includesOrphanedProducts = (rows: StoryRow[]) => {
  const orphanedProductRowIndex = rows.findIndex(isOrphanedProductRow)
  if (process.env.NODE_ENV !== 'production') {
    const orphanedProductRowCount = rows.filter(isOrphanedProductRow).length
    if (orphanedProductRowCount > 1) {
      throw new Error(
        `Only one orphaned product row per story is allowed, found ${orphanedProductRowCount}`,
      )
    }
    if (orphanedProductRowIndex > 0) {
      throw new Error(
        `Orphaned product row must always be at the first position, found at index ${orphanedProductRowIndex}`,
      )
    }
  }
  return orphanedProductRowIndex === 0
}

const injectOrphanedRowIntoRowOrder = (
  rowOrder: StoryRowOrder,
): StoryRowOrder => {
  const nextRowOrder = rowOrder.map(row => ({ ...row, index: row.index + 1 }))

  nextRowOrder.unshift({ type: 'content', index: 0 })
  return nextRowOrder
}

const deleteOrphanedProductRowFromInterleaveRow = (
  interleaveRows: InterleaveRow[],
): InterleaveRow[] => {
  return interleaveRows.slice(1).map(row => ({ ...row, index: row.index - 1 }))
}

export const getIsEditorialPresent = ({
  pageIsFiltered,
  categoryId,
  sortId,
}: {
  pageIsFiltered: boolean
  categoryId: string | null | undefined
  sortId: ProductSortingIdType
}) => {
  return !pageIsFiltered && !categoryId && sortId === DEFAULT_PRODUCT_SORTING_ID
}
